import React from 'react'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import { ALL_METRICS } from '../../constants/constantsAnalytic'
import '../styles/Ranking.css'

const METRIC_LABELS = {
  alarms_count: { key: 'alarms', fallback: 'Alarmas' },
  fasts_count: { key: 'fasts', fallback: 'Ayunos' },
  perdidos_count: { key: 'pellets_lost', fallback: 'PPA' },
  visitas_count: { key: 'visits', fallback: 'Visitas' },
};

const resolveTranslation = (translate, key, fallback, params) => {
  const value = typeof translate === 'function' ? translate(key, params) : key;
  return value && value !== key ? value : fallback;
};

const getMetricLabel = (key, translate) => {
  const config = METRIC_LABELS[key];
  if (!config) return key;
  return resolveTranslation(translate, config.key, config.fallback);
};

// Styled Tooltip wrapper for Jaula container
const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    disablePortal
    PopperProps={{
      modifiers: [
        { name: 'preventOverflow', options: { boundary: 'scrollParent' } },
        { name: 'flip', options: { boundary: 'scrollParent' } }
      ]
    }}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: 'transparent'
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    padding: 0,
    maxWidth: 'none'
  }
}))

// Card container for tooltip content
const TooltipCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.96)',
  border:'solid 0.01vw #e94e1a',
  color: '#a1a1a1ff',
  borderRadius: theme.spacing(1),
  minWidth: '20vw'
}))

// Tooltip content rendering metrics in a table
const TooltipContent = ({ valuesSummary = [], selectedDay, name, fastingResolution, translate }) => {
  const metricsConfig = [
    { key: 'alarms_count', fallbackColor: 'rgb(128, 80, 32)' },
    { key: 'fasts_count', fallbackColor: 'rgb(117, 40, 60)' },
    { key: ['perdidos_count'], fallbackColor: 'rgb(68, 112, 161)' }
    // { key: 'perdidos_count', fallbackLabel: 'PPA',     fallbackColor: 'rgb(68, 112, 161)' }
  ]

    // helper: devuelve el primer valor definido (no null/undefined) entre varios keys
  const pickFirst = (obj, keys) => {
    const list = Array.isArray(keys) ? keys : [keys];
    for (const k of list) {
      const v = obj?.[k];
      if ((v ?? false) || v === 0) return v; // acepta 0 como válido
    }
    return undefined;
  };
  const normalizeFastValue = (value) =>
    fastingResolution === 'day' && Number(value) === 0.5 ? 0 : (value ?? 0);

  // helper: encuentra la definición en ALL_METRICS que matchee cualquiera de los aliases
  const findMetricDef = (keys) => {
    const list = Array.isArray(keys) ? keys : [keys];
    return ALL_METRICS.find(m => {
      const mKeys = Array.isArray(m.key) ? m.key : [m.key];
      return mKeys.some(k => list.includes(k));
    });
  };

  // Build columns with title, color, and data
  const columns = metricsConfig.map(({ key, fallbackColor }) => {
    const metric = findMetricDef(key);//ALL_METRICS.find(m => m.key === key)
    const metricKey = Array.isArray(key) ? key[0] : key;
    const title = getMetricLabel(metricKey, translate)
    const color = metric?.color || fallbackColor
    // const data = valuesSummary
    //   .filter(i => i.value[key] > 0)
    //   .map(i => ({ day: i.day, count: i.value[key] }))
    const data = valuesSummary
      .map(i => ({
        day: i.day,
        count:
          (Array.isArray(key) ? key[0] : key) === 'fasts_count'
            ? normalizeFastValue(pickFirst(i.value, key))
            : (pickFirst(i.value, key) ?? 0)
      }))
      .filter(row => row.count > 0);

    // usa el primer alias como key "canónica" para la columna
    const canonKey = Array.isArray(key) ? key[0] : key;
    
    return { key: canonKey, title, color, data };
    // return { key, title, color, data }
  })

  const hasDate = typeof selectedDay === 'string' && selectedDay

  // Determine which days to list
  const allDays = hasDate
    ? [selectedDay]
    : Array.from(new Set(valuesSummary.map(i => i.day)))
        .sort((a, b) => new Date(a) - new Date(b))

  return (
    <TooltipCard elevation={0}>
      <CardContent style={{padding:'1.5vw', maxHeight:'45vh', overflowY:'auto'}}>
        <Typography
          variant="subtitle2"
          gutterBottom
          style={{ fontSize: '.9vw', color: '#a1a1a1ff' }}
        >
          {name}{hasDate ? ` - ${selectedDay}` : ''}
        </Typography>

        <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none' } }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ color: '#a1a1a1ff', fontSize: '.8vw',fontWeight:800, borderBottom:'solid 0.01vh #a1a1a17a' }}>
                {resolveTranslation(translate, 'fecha', 'Fecha')}
              </TableCell>
              {columns.map(col => (
                <TableCell
                  key={col.key}
                  style={{ color: col.color, fontSize: '.85vw', fontWeight:800, borderBottom:'solid 0.01vh #a1a1a17a' }}
                >
                  {col.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {allDays.map(day => (
              <TableRow key={day} style={{borderBottom:'solid 0.01vh #a1a1a17a'}} >
                <TableCell>
                  <strong style={{ color: '#a1a1a1ff', fontSize: '.75vw' }}>
                    {day}
                  </strong>
                </TableCell>
                {columns.map(col => {
                  const item = col.data.find(d => d.day === day)
                  return (
                    <TableCell
                      key={col.key}
                      style={{
                        fontSize: '.75vw',
                        color: item ? col.color : '#a1a1a1ff'
                      }}
                    >
                      {item ? item.count : '--'}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </TooltipCard>
  )
}

// ProgressBar uses metric color
function ProgressBar({ percent, color, animate }) {
  return (
    <div
      className={`progress-bar ${animate ? 'animate-priority' : ''}`}
      style={{
        background: 'rgba(255,255,255,0.3)',
        borderRadius: 5,
        overflow: 'hidden',
        width: '7.5vw'
      }}
    >
      <div
        style={{
          width: `${percent}%`,
          backgroundColor: color,
          height: '2vh',
          transition: 'width 0.5s ease'
        }}
      />
    </div>
  )
}

// Main Jaula component with dynamic metrics
function Jaula({
  name,
  viewMode,
  selectedDay,
  values_summary: valuesSummary = [],
  priority,
  maxAlarmValue,
  maxFastValue,
  maxPPAValue,
  displayAlarms: alarmValue,
  displayFasts: fastValue,
  displayPPA: ppaValue,
  fastingResolution,
  translate
}) {
  const normalizedFastValue =
    fastingResolution === 'day' && Number(fastValue) === 0.5 ? 0 : fastValue
  const getFastingText = v =>
    v === 0    ? resolveTranslation(translate, 'no', 'No')
    : v === 0.5 ? resolveTranslation(translate, 'half_day', 'Medio día')
    : v >= 1    ? resolveTranslation(translate, 'si', 'Sí')
                : v

  // Calculate percentages
  const porcentaje     = maxAlarmValue ? (alarmValue   / maxAlarmValue) * 100 : 0
  const porcentajeFast = maxFastValue  ? (normalizedFastValue / maxFastValue)  * 100 : 0
  const porcentajePPA  = maxPPAValue   ? (ppaValue     / maxPPAValue)   * 100 : 0

  // Prepare items
  const itemsConfig = [
    { key: 'alarms_count',   value: alarmValue, percent: porcentaje    },
    { key: 'fasts_count',    value: normalizedFastValue,  percent: porcentajeFast},
    { key: 'perdidos_count', value: ppaValue,   percent: porcentajePPA }
  ]

  const items = itemsConfig.map(({ key, value, percent }) => {
    const metric = ALL_METRICS.find(m => m.key === key)
    const label  = getMetricLabel(key, translate)
    const color  = metric?.color || (key === 'fasts_count' ? 'rgb(117, 40, 60)' : '#ccc')
    const displayText =
      key === 'fasts_count'
        ? viewMode === 'day'
          ? `${resolveTranslation(translate, 'fasts', 'Ayunos')}: ${getFastingText(value)}`
          : `${value} ${resolveTranslation(translate, 'fasts', 'Ayunos')}`
        : `${value} ${label}`

    return {
      key,
      jsx: (
        <>
          <ProgressBar percent={percent} color={color} animate={priority === key} />
          <Typography variant="body2" style={{ marginLeft: '0.5vw', fontSize: '.6vw' }}>
            {displayText}
          </Typography>
        </>
      )
    }
  })

  // Reorder by priority
  const ordered = priority
    ? [
        ...items.filter(item => item.key === priority),
        ...items.filter(item => item.key !== priority)
      ]
    : items

  return (
    <BootstrapTooltip
      title={
        <TooltipContent
          valuesSummary={valuesSummary}
          selectedDay={selectedDay}
          name={name}
          fastingResolution={fastingResolution}
          translate={translate}
        />
      }
      placement="left"
      arrow
    >
      <Grid
        container
        alignItems="center"
        style={{
          padding: '0.5vw',
          width: '14vw',
          height: '14vh',
          border: '0.1vh solid #ccc',
          borderRadius: '0.75vh'
        }}
      >
        <Grid container justifyContent="flex-start" alignItems="center" style={{ height: '2vh' }}>
          <Typography variant="subtitle2" style={{ color: '#a1a1a1ff', fontSize: '.8vw' }}>
            <strong>{name}</strong>
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          {ordered.map(item => (
            <Grid
              key={item.key}
              item
              xs={12}
              style={{ display: 'flex', alignItems: 'center', height: '3vh' }}
            >
              {item.jsx}
            </Grid>
          ))}
        </Grid>
      </Grid>
    </BootstrapTooltip>
  )
}

export default Jaula
